import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowLeft, Search, FileText, MessageSquare, Calendar, Clock } from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import SiteFooter from '../components/SiteFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SearchPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    const initialQuery = searchParams.get('q');
    if (initialQuery) {
      setQuery(initialQuery);
      performSearch(initialQuery);
    }
    // Mount-only: kick off initial search from URL once. Subsequent searches
    // are user-driven via the form; adding searchParams as a dep would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const performSearch = async (searchQuery) => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      toast.error('Please enter at least 2 characters');
      return;
    }

    setLoading(true);
    setHasSearched(true);

    try {
      const token = localStorage.getItem('sage_token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(`${API}/search`, {
        params: { q: searchQuery.trim() },
        headers: { Authorization: `Bearer ${token}` }
      });

      setResults(response.data);
      setSearchParams({ q: searchQuery.trim() });
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    performSearch(query);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return '';
    }
  };

  const highlightMatch = (text, query) => {
    if (!text || !query) return text;
    const words = query.toLowerCase().split(' ');
    let result = text;
    words.forEach(word => {
      if (word.length > 1) {
        const regex = new RegExp(`(${word})`, 'gi');
        result = result.replace(regex, '<mark class="bg-yellow-500/30 text-white">$1</mark>');
      }
    });
    return result;
  };

  return (
    <div className="min-h-screen bg-obsidian">
      <div className="overlay-noise" />
      
      <div className="relative z-10">
        {/* Header */}
        <div className="border-b border-white/5 bg-stone-950/50 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto px-6 py-6">
            <div className="flex items-center gap-4 mb-6">
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-transparent border border-white/20 text-white hover:bg-white/5 rounded-sm px-4 py-2 transition-all"
                data-testid="back-button"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div className="flex-1">
                <h1 
                  className="text-2xl font-light tracking-tight text-zinc-100"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  Search Your Wisdom
                </h1>
                <p className="text-xs font-mono uppercase tracking-widest text-zinc-400 mt-1">
                  Sessions & Knowledge Anchors
                </p>
              </div>
              <ThemeToggle className="border border-white/20 rounded-sm" />
            </div>

            {/* Search Form */}
            <form onSubmit={handleSubmit} className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search sessions, insights, knowledge..."
                  className="w-full bg-stone-950/50 border border-white/10 text-white placeholder:text-zinc-500 pl-12 pr-4 py-3 rounded-sm focus:outline-none focus:border-white/30"
                  data-testid="search-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="frost-button rounded-sm px-6 py-3"
                data-testid="search-button"
              >
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </form>
          </div>
        </div>

        {/* Results */}
        <div className="max-w-4xl mx-auto px-6 py-8">
          {loading && (
            <div className="text-center py-12">
              <p className="text-zinc-500">Searching...</p>
            </div>
          )}

          {!loading && hasSearched && results && (
            <div className="space-y-8">
              {/* Results Summary */}
              <div className="text-sm text-zinc-500">
                Found {results.total_results} result{results.total_results !== 1 ? 's' : ''} for "{results.query}"
              </div>

              {results.total_results === 0 && (
                <div className="text-center py-12 bg-stone-950/50 border border-white/5 rounded-sm">
                  <p className="text-zinc-400">No results found</p>
                  <p className="text-zinc-400 text-sm mt-2">Try different keywords or check your spelling</p>
                </div>
              )}

              {/* Session Results */}
              {results.sessions.length > 0 && (
                <div>
                  <h2 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Sessions ({results.sessions.length})
                  </h2>
                  <div className="space-y-3">
                    {results.sessions.map((session) => (
                      <div
                        key={session.session_id}
                        onClick={() => navigate(`/sessions?highlight=${session.session_id}`)}
                        className="bg-stone-950/50 border border-white/5 rounded-sm p-5 cursor-pointer hover:border-white/10 transition-all"
                        data-testid={`session-result-${session.session_id}`}
                      >
                        <div className="flex items-center gap-3 text-xs text-zinc-500 mb-2">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(session.created_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {session.duration_minutes}m
                          </span>
                        </div>
                        <p 
                          className="text-sm text-zinc-300 leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: highlightMatch(session.preview, query) }}
                        />
                        {session.insights?.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {session.insights.slice(0, 2).map((insight, i) => (
                              <span 
                                key={`${session.session_id}-insight-${i}`}
                                className="text-xs bg-white/5 text-zinc-400 px-2 py-1 rounded"
                                dangerouslySetInnerHTML={{ __html: highlightMatch(insight.slice(0, 50) + '...', query) }}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Knowledge Anchor Results */}
              {results.anchors.length > 0 && (
                <div>
                  <h2 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Knowledge Anchors ({results.anchors.length})
                  </h2>
                  <div className="space-y-3">
                    {results.anchors.map((anchor) => (
                      <div
                        key={anchor.anchor_id}
                        className="bg-stone-950/50 border border-white/5 rounded-sm p-5 hover:border-white/10 transition-all"
                        data-testid={`anchor-result-${anchor.anchor_id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h3 
                            className="text-white font-medium"
                            dangerouslySetInnerHTML={{ __html: highlightMatch(anchor.title, query) }}
                          />
                          <span className="text-xs text-zinc-400 font-mono">
                            {anchor.source_type?.toUpperCase()} • {anchor.weight}%
                          </span>
                        </div>
                        <p 
                          className="text-sm text-zinc-400 leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: highlightMatch(anchor.preview, query) }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {!hasSearched && (
            <div className="text-center py-16">
              <Search className="w-12 h-12 text-zinc-500 mx-auto mb-4" />
              <p className="text-zinc-500">Search through your coaching sessions and knowledge anchors</p>
              <p className="text-zinc-400 text-sm mt-2">Find insights, commitments, and wisdom from your journey</p>
            </div>
          )}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
};

export default SearchPage;
